import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDataSyncJob } from './queryKeys';

type RunTaskParams = {
  taskId: string;
  testRun?: boolean;
  dryRun?: boolean;
  /** Skip the success toast, e.g. when a dialog already shows the progress. */
  suppressToast?: boolean;
};

type RunTaskResponse = {
  message: string;
};

/**
 * Triggers a manual run of a data sync task.
 */
export const runDataSyncTask = async ({
  taskId,
  testRun = false,
  dryRun = false,
}: RunTaskParams): Promise<RunTaskResponse> => {
  const response = await vestigasApi
    .post(`data_sync/sync_task/${taskId}/manual`, {
      json: {
        testRun,
        dryRun,
      },
    })
    .json<RunTaskResponse>();
  return response;
};

/**
 * React Query mutation hook for triggering a manual data sync task run.
 * Automatically invalidates log queries on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationRunTask = (
  options?: Omit<
    UseMutationOptions<RunTaskResponse, Error, RunTaskParams>,
    'mutationFn'
  >,
) => {
  const { t } = useTranslation('dataSync');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: runDataSyncTask,
    async onSuccess(_data, variables) {
      // Invalidate log queries to show the new run
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.getAllLogs(),
      });

      if (variables.suppressToast) {
        return;
      }

      const message = variables.dryRun
        ? t(($) => $.toast.dryRunStarted)
        : variables.testRun
          ? t(($) => $.toast.testRunStarted)
          : t(($) => $.toast.runStarted);
      ToastService.success(message);
    },
  });

  return mutation;
};
